// Suggestions, applied in memory (docs/ai-connections-brief.md, Part 2 outcome c): "price if all approved" is the
// job resolved with every waiting suggestion laid over its rows, never stored. A library suggestion changes the
// library, not the job, so the preview leaves it out and says so. Pure: rows in, rows out.

import type { CostLineIn, CustomBomLine, CustomRouteLine, JobBomLineIn, JobIn, JobItemIn, JobRouteLineIn, LibMaterial, Library, Scalar, SubLineBuildCell } from './types.ts';

/**
 * The kinds. 2026-10-18 (Spencer: "let an AI employee suggest changes to one job item's own route and BOM"):
 * item_route_step adds, changes or removes a step on one job item (a custom item's own route, or a catalog-built
 * item's own lines beside its recipe, the catalog left alone); item_bom_line the same for a material line, naming a
 * library material or a new_material suggestion not yet approved; hold_value holds a value on an input that otherwise
 * follows a standard; new_material is a material for the library (name, vendor, price, pack size, link), so a missing
 * part does not wait on Spencer. 2026-10-18 (Spencer: scope rows through the connector): scope_reword, scope_link, scope_add
 * and scope_dismiss change a scope row's words, the item it became, add one, or dismiss one with a reason; a scope row is
 * words, not price, so the preview has nothing to lay over the job for them and skips none. 2026-10-18 (Spencer: a catalog item itself,
 * through the Library's queue): library_route_step, library_bom_line, library_part, library_input and library_calculation add, change or
 * remove one row of a catalog item (a recipe's step, line or part; the item's input or calculation); like every library kind they change
 * the library, not a job, so the job preview leaves them out and says so. 2026-10-19 (Spencer: "let the connectors suggest changes to a
 * job's job-wide hours and its job-wide materials and costs, the same way they can already suggest changes to items"): job_route_step
 * types a quantity against a step of the job route (one of the job type's steps, or a step the job added) or adds or removes one;
 * job_bom_line the same for the job's own material lines; job_cost_line adds, changes or removes a job-wide cost (tax, shipping,
 * handling on a vendor order). The preview lays each over the job's routeLines, bomLines and costLines.
 */
export type SuggestionKind =
  | 'add_job_item' | 'change_input' | 'set_job_standard' | 'type_price' | 'move_item' | 'create_add_on' | 'custom_line'
  | 'item_route_step' | 'item_bom_line' | 'hold_value' | 'new_material'
  | 'job_route_step' | 'job_bom_line' | 'job_cost_line'
  | 'scope_reword' | 'scope_link' | 'scope_add' | 'scope_dismiss'
  | 'library_recipe_line' | 'library_operation' | 'library_time_standard'
  | 'library_route_step' | 'library_bom_line' | 'library_part' | 'library_input' | 'library_calculation'
  // 2026-10-23 (docs/ai-employee-needs-brief.md): the twins of the screens' writes; none changes a price the preview could lay over the job
  | 'create_job' | 'edit_item' | 'retire_item' | 'restore_item' | 'room_add' | 'room_rename' | 'edit_custom_line'
  | 'build_create' | 'build_edit' | 'package_create' | 'package_edit' | 'add_on_edit'
  | 'scope_source' | 'scope_outcome' | 'scope_tag' | 'mark_place' | 'mark_remove' | 'edit_job' | 'room_standard' | 'job_pricing'
  | 'library_material' | 'library_item' | 'library_child'
  // 2026-10-24: the Views pack; 2026-10-25: a sub line added or edited by an AI employee (the second pair moves a price)
  | 'view_edit' | 'view_remove' | 'view_restore' | 'sub_line_add' | 'sub_line_edit';

/** The five kinds that change a catalog item's own rows (2026-10-18), and the table each one changes. */
export const CATALOG_SUGGESTION_TABLES = {
  library_route_step: 'recipe_route_step',
  library_bom_line: 'recipe_bom_line',
  library_part: 'catalog_part',
  library_input: 'catalog_input',
  library_calculation: 'catalog_derived',
  // a child ("makes") row (2026-10-23)
  library_child: 'catalog_child',
} as const;
export type CatalogSuggestionKind = keyof typeof CATALOG_SUGGESTION_TABLES;
export const isCatalogSuggestionKind = (kind: unknown): kind is CatalogSuggestionKind => typeof kind === 'string' && kind in CATALOG_SUGGESTION_TABLES;

/** The columns a catalog suggestion may change on each table: what the catalog editor lets a person type (the same list as ug.library_columns). */
export const CATALOG_SUGGESTION_COLUMNS: Record<(typeof CATALOG_SUGGESTION_TABLES)[CatalogSuggestionKind], readonly string[]> = {
  recipe_route_step: ['operation_id', 'qty_expr', 'charge_setup', 'note'],
  recipe_bom_line: ['material_id', 'material_role', 'label', 'qty_expr', 'unit_price_expr', 'separate_pricing', 'markup_pct', 'ignore_margin', 'sub_expr', 'link_expr', 'note'],
  catalog_part: ['name', 'label', 'qty_expr', 'length_expr', 'width_expr', 'material_id', 'material_role', 'grain', 'band_long_expr', 'band_short_expr', 'band_material_expr', 'grain_locked', 'note'],
  catalog_input: ['key', 'label', 'unit', 'data_type', 'default_value', 'choices', 'rule_expr', 'rule_message', 'info', 'material_filter', 'standard_key', 'hidden_expr'],
  catalog_derived: ['key', 'label', 'expr'],
  catalog_child: ['label', 'child_catalog_item_id', 'child_expr', 'count_expr', 'input_map'],
};

/** What a line suggestion does: add a line, change one (an own line by index, or a recipe step or line by its id), remove one. */
export interface LineChange {
  change: 'add' | 'change' | 'remove';
  /** The index of the item's own line it changes or removes, when it is one of those. */
  at?: number | null;
  /** The recipe step (item_route_step) or recipe BOM line (item_bom_line) it changes or removes, when it is one of the recipe's. */
  step_id?: string | null;
  line_id?: string | null;
  /** The line as it will be stored (operation_id / material_id, qty, note, …); the quantity in the box wins over its qty. */
  line?: Record<string, unknown>;
  /** item_bom_line: the new_material suggestion whose material this line buys, when the material is not in the library yet. */
  material_suggestion_id?: string | null;
}

/**
 * What a job-wide line suggestion does (2026-10-19): add a step, line or cost; change one (a step of the job type's route by
 * type_step_id, a line the job added or already typed by line_id, a cost by its row id); remove one. Removing a step of the job
 * type's route types 0 against it on this job (the type's step stays listed); removing a line the job added or a cost drops the row.
 */
export interface JobLineChange {
  change: 'add' | 'change' | 'remove';
  /** job_route_step: the job type's step it types against. job_bom_line: the type's line (type_line_id). */
  type_step_id?: string | null;
  type_line_id?: string | null;
  /** The job's own row it changes or removes (job_route_line, job_bom_line, job_cost_line), when there is one. */
  line_id?: string | null;
  /** The line as it will be stored (operation_id / material_id / label, qty or amount, separate_pricing / markup_pct / ignore_margin, note); the value in the box wins over its qty or amount. */
  line?: Record<string, unknown>;
}

export interface SuggestionIn {
  id: string;
  kind: SuggestionKind;
  target_table: string | null;
  target_id: string | null;
  target_key: string | null;
  new_value: unknown;
  payload: Record<string, unknown>;
  status: string;
}

export interface Applied {
  jobIn: JobIn;
  applied: string[];
  /** Suggestions the preview could not lay over the job, each with why. */
  skipped: { id: string; why: string }[];
  /** Materials the waiting new_material suggestions would add to the library, under the id suggested:<suggestion id>; libraryWith lays them over a library. */
  materials: LibMaterial[];
}

/** The library with the preview's suggested materials in it, so a line that buys one prices; the library given is not changed. */
export function libraryWith(lib: Library, applied: Pick<Applied, 'materials'>): Library {
  if (applied.materials.length === 0) return lib;
  const materials = { ...lib.materials };
  for (const m of applied.materials) materials[m.id] = m;
  return { ...lib, materials };
}

/** The id a suggested material prices under in the preview. */
export const suggestedMaterialId = (suggestionId: string) => `suggested:${suggestionId}`;

/** A line suggestion applied to a list of own lines (route or BOM), the way ug.approve_suggestion stores it. Returns null when it cannot. */
export function applyLineChange<T extends CustomRouteLine | CustomBomLine>(lines: T[], c: LineChange, qtyInBox: unknown): T[] | null {
  const q = qtyInBox === null || qtyInBox === undefined || String(qtyInBox).trim() === '' ? undefined : (qtyInBox as Scalar);
  const line = { ...(c.line ?? {}) } as unknown as T;
  if (q !== undefined) (line as { qty: Scalar }).qty = q;
  const key = c.step_id ? 'step_id' : c.line_id ? 'line_id' : null;
  const id = c.step_id ?? c.line_id ?? null;
  if (c.change === 'add') return [...lines, line];
  if (typeof c.at === 'number') {
    if (c.at < 0 || c.at >= lines.length) return null;
    const was = lines[c.at]!;
    if (c.change === 'remove') {
      // an own change to a recipe step turns into a removal of that step; an added line simply goes
      const k = (was as unknown as Record<string, unknown>).step_id ? 'step_id' : (was as unknown as Record<string, unknown>).line_id ? 'line_id' : null;
      if (k) return lines.map((l, i) => (i === c.at ? ({ ...l, removed: true } as T) : l));
      return lines.filter((_, i) => i !== c.at);
    }
    return lines.map((l, i) => (i === c.at ? ({ ...l, ...line } as T) : l));
  }
  if (!key || !id) return null;
  // a recipe step or line: the change is a new own line naming it (an older one for the same step is replaced)
  const rest = lines.filter((l) => (l as unknown as Record<string, unknown>)[key] !== id);
  if (c.change === 'remove') return [...rest, { ...line, [key]: id, removed: true } as T];
  return [...rest, { ...line, [key]: id } as T];
}

const scalar = (v: unknown): Scalar => (v === undefined ? null : typeof v === 'object' && v !== null ? JSON.stringify(v) : (v as Scalar));

const blankish = (v: unknown) => v === null || v === undefined || String(v).trim() === '';

/**
 * A job-wide line suggestion laid over the job's route or BOM lines, the way ug.approve_suggestion stores it. The line a change
 * names is found by the job's own row (line_id) or by the type's step or line it types against; null when it is gone.
 */
export function applyJobLineChange<T extends JobRouteLineIn | JobBomLineIn>(lines: T[], c: JobLineChange, qtyInBox: unknown, newId: string, typeKey: 'type_step_id' | 'type_line_id'): T[] | null {
  const typeId = (c[typeKey] ?? null) as string | null;
  const line = { ...(c.line ?? {}) } as Record<string, unknown>;
  const qty = blankish(qtyInBox) ? (line.qty as Scalar | undefined) : (qtyInBox as Scalar);
  if (c.change === 'add') {
    return [...lines, { ...line, id: newId, qty: qty ?? null, note: (line.note as string | null | undefined) ?? null, [typeKey]: null } as unknown as T];
  }
  const at = lines.findIndex((l) => (c.line_id && l.id === c.line_id) || (typeId && ((l as unknown as Record<string, unknown>)[typeKey] === typeId || l.id === `type:${typeId}`)));
  if (at < 0) return null;
  const was = lines[at]!;
  if (c.change === 'remove') {
    // a step or line of the job type's own route stays listed: this job types 0 against it; one the job added simply goes
    const ofType = (was as unknown as Record<string, unknown>)[typeKey];
    return ofType ? lines.map((l, i) => (i === at ? ({ ...l, qty: '0' } as T) : l)) : lines.filter((_, i) => i !== at);
  }
  const { qty: _q, ...rest } = line;
  return lines.map((l, i) => (i === at ? ({ ...l, ...rest, qty: qty ?? l.qty } as T) : l));
}

/** The job with every waiting suggestion applied over its rows: the preview's input. */
export function applySuggestions(jobIn: JobIn, suggestions: SuggestionIn[]): Applied {
  const items = jobIn.items.map((i) => ({ ...i, inputs: { ...i.inputs }, overrides: { ...i.overrides }, custom_route: [...i.custom_route], custom_bom: [...i.custom_bom] }));
  const subLines = jobIn.subLines.map((s) => ({ ...s }));
  const standards = { ...jobIn.standards };
  // the job's own lines (2026-10-19), copied so the rows given stay as they were; they ride along only when the job had them or a suggestion touched them
  let routeLines: JobRouteLineIn[] = (jobIn.routeLines ?? []).map((l) => ({ ...l }));
  let bomLines: JobBomLineIn[] = (jobIn.bomLines ?? []).map((l) => ({ ...l }));
  let costLines: CostLineIn[] = (jobIn.costLines ?? []).map((l) => ({ ...l }));
  const touched = { routeLines: false, bomLines: false, costLines: false };
  const applied: string[] = [];
  const skipped: { id: string; why: string }[] = [];
  const materials: LibMaterial[] = [];
  for (const s of suggestions) {
    if (s.status !== 'waiting') continue;
    switch (s.kind) {
      case 'change_input':
      case 'type_price': {
        if (s.target_table === 'sub_line') {
          const line = subLines.find((l) => l.id === s.target_id);
          if (!line) {
            skipped.push({ id: s.id, why: 'its sub line is no longer on the job' });
            break;
          }
          if (s.target_key === 'quote_amount') line.quote_amount = scalar(s.new_value);
          else line.internal_estimate = scalar(s.new_value);
          applied.push(s.id);
          break;
        }
        const item = items.find((i) => i.id === s.target_id);
        if (!item || !s.target_key) {
          skipped.push({ id: s.id, why: 'its item is no longer on the job' });
          break;
        }
        item.inputs[s.target_key] = scalar(s.new_value);
        applied.push(s.id);
        break;
      }
      case 'set_job_standard': {
        if (!s.target_key) {
          skipped.push({ id: s.id, why: 'it names no standard' });
          break;
        }
        standards[s.target_key] = scalar(s.new_value);
        applied.push(s.id);
        break;
      }
      case 'add_job_item': {
        const p = s.payload;
        if (typeof p.catalog_item_id !== 'string') {
          skipped.push({ id: s.id, why: 'it names no catalog item' });
          break;
        }
        items.push(bareItem(`suggested:${s.id}`, p, items.length + 1));
        applied.push(s.id);
        break;
      }
      case 'move_item': {
        // the item goes into the add-on: it leaves the base price (2026-10-17)
        const item = items.find((i) => i.id === s.target_id);
        const addOn = typeof s.payload.add_on_id === 'string' ? s.payload.add_on_id : null;
        if (!item || !addOn) {
          skipped.push({ id: s.id, why: 'its item or its add-on is no longer on the job' });
          break;
        }
        item.add_on_id = addOn;
        applied.push(s.id);
        break;
      }
      case 'create_add_on': {
        // a new add-on: its moved items leave the base, its alternates are priced only with it on, so the base moves too
        const addOn = `suggested:${s.id}`;
        const moves = Array.isArray(s.payload.move_item_ids) ? (s.payload.move_item_ids as unknown[]).map(String) : [];
        if (moves.some((id) => !items.some((i) => i.id === id))) {
          skipped.push({ id: s.id, why: 'an item it moves is no longer on the job' });
          break;
        }
        for (const i of items) if (moves.includes(i.id)) i.add_on_id = addOn;
        const alternates = Array.isArray(s.payload.alternates) ? (s.payload.alternates as Record<string, unknown>[]) : [];
        alternates.forEach((a, k) => {
          items.push({ ...bareItem(`suggested:${s.id}:${k}`, a, items.length + 1), add_on_id: addOn, replaces_item_id: typeof a.replaces_item_id === 'string' ? a.replaces_item_id : null });
        });
        applied.push(s.id);
        break;
      }
      case 'hold_value': {
        // a value held on the item over the standard it follows (2026-10-18)
        const item = items.find((i) => i.id === s.target_id);
        if (!item || !s.target_key) {
          skipped.push({ id: s.id, why: 'its item is no longer on the job' });
          break;
        }
        item.overrides[s.target_key] = scalar(s.new_value);
        applied.push(s.id);
        break;
      }
      case 'item_route_step': {
        const item = items.find((i) => i.id === s.target_id);
        if (!item) {
          skipped.push({ id: s.id, why: 'its item is no longer on the job' });
          break;
        }
        const next = applyLineChange(item.custom_route, s.payload as unknown as LineChange, s.new_value);
        if (!next) {
          skipped.push({ id: s.id, why: 'the step it changes is no longer on the item' });
          break;
        }
        item.custom_route = next;
        applied.push(s.id);
        break;
      }
      case 'item_bom_line': {
        const item = items.find((i) => i.id === s.target_id);
        if (!item) {
          skipped.push({ id: s.id, why: 'its item is no longer on the job' });
          break;
        }
        const c = { ...(s.payload as unknown as LineChange) };
        // a material the library does not have yet: the preview prices it from its own suggestion
        if (c.material_suggestion_id) c.line = { ...(c.line ?? {}), material_id: suggestedMaterialId(c.material_suggestion_id) };
        const next = applyLineChange(item.custom_bom, c, s.new_value);
        if (!next) {
          skipped.push({ id: s.id, why: 'the line it changes is no longer on the item' });
          break;
        }
        item.custom_bom = next;
        applied.push(s.id);
        break;
      }
      case 'new_material': {
        const p = s.payload;
        const t = (v: unknown) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim());
        materials.push({
          id: suggestedMaterialId(s.id),
          admin005_id: null,
          description: t(p.description) ?? '(new material)',
          price: t(s.new_value),
          unit: t(p.unit),
          vendor: t(p.vendor),
          category: t(p.category),
          subcategory: t(p.subcategory),
          verified: false,
          // a pack filed as the pack's price with its size (2026-10-19); one filed before carries the price per piece
          ...(p.priced_per_pack === true ? { pack_size: t(p.pack_size), whole_packs: p.whole_packs === true } : {}),
        });
        applied.push(s.id);
        break;
      }
      case 'custom_line': {
        const line = customLine(s.payload.line, s.new_value);
        if (s.target_id) {
          // filling an item: a custom item's own line, or a typed line beside a catalog item's recipe (2026-10-18)
          const item = items.find((i) => i.id === s.target_id);
          if (!item) {
            skipped.push({ id: s.id, why: 'the item to fill is no longer on the job' });
            break;
          }
          item.custom_bom.push(line);
        } else {
          const p = s.payload;
          items.push({
            ...bareItem(`suggested:${s.id}`, { number: p.number, name: p.name, room_id: p.parent_item_id ? null : p.room_id }, items.length + 1),
            parent_item_id: typeof p.parent_item_id === 'string' ? p.parent_item_id : null,
            is_custom: true,
            custom_bom: [line],
          });
        }
        applied.push(s.id);
        break;
      }
      case 'job_route_step': {
        // a step of the job route (2026-10-19): a quantity typed against one of the type's steps or a step the job added, a step added, or one removed
        const next = applyJobLineChange(routeLines, s.payload as unknown as JobLineChange, s.new_value, `suggested:${s.id}`, 'type_step_id');
        if (!next) {
          skipped.push({ id: s.id, why: 'the step it changes is no longer on the job route' });
          break;
        }
        routeLines = next;
        touched.routeLines = true;
        applied.push(s.id);
        break;
      }
      case 'job_bom_line': {
        const next = applyJobLineChange(bomLines, s.payload as unknown as JobLineChange, s.new_value, `suggested:${s.id}`, 'type_line_id');
        if (!next) {
          skipped.push({ id: s.id, why: 'the line it changes is no longer on the job BOM' });
          break;
        }
        bomLines = next;
        touched.bomLines = true;
        applied.push(s.id);
        break;
      }
      case 'job_cost_line': {
        // a job-wide cost (tax, shipping, handling on a vendor order): added, its amount changed, or removed
        const c = s.payload as unknown as JobLineChange;
        const line = (c.line ?? {}) as Record<string, unknown>;
        const amount = blankish(s.new_value) ? ((line.amount as Scalar | undefined) ?? null) : (s.new_value as Scalar);
        touched.costLines = true;
        if (c.change === 'add') {
          costLines.push({ id: `suggested:${s.id}`, label: String(line.label ?? 'Job-wide cost'), amount, separate_pricing: line.separate_pricing === true, ignore_margin: line.ignore_margin === true, markup_pct: (line.markup_pct as number | string | null | undefined) ?? null });
          applied.push(s.id);
          break;
        }
        const at = costLines.findIndex((l) => l.id === c.line_id);
        if (at < 0) {
          skipped.push({ id: s.id, why: 'the job-wide cost it changes is no longer on the job' });
          break;
        }
        if (c.change === 'remove') costLines = costLines.filter((_, i) => i !== at);
        else costLines[at] = { ...costLines[at]!, amount, ...(typeof line.separate_pricing === 'boolean' ? { separate_pricing: line.separate_pricing } : {}), ...(typeof line.ignore_margin === 'boolean' ? { ignore_margin: line.ignore_margin } : {}), ...(line.markup_pct !== undefined ? { markup_pct: line.markup_pct as number | string | null } : {}), ...(typeof line.label === 'string' && line.label ? { label: line.label } : {}) };
        applied.push(s.id);
        break;
      }
      case 'sub_line_add': {
        // a sub line on the job or an item (2026-10-25): as filed, the builds it is not in left out by hand
        const l = (s.payload.line ?? {}) as Record<string, unknown>;
        const itemId = typeof l.job_item_id === 'string' ? l.job_item_id : null;
        if (itemId && !items.some((i) => i.id === itemId)) {
          skipped.push({ id: s.id, why: 'the item it belongs to is no longer on the job' });
          break;
        }
        subLines.push({
          id: `suggested:${s.id}`,
          scope_text: String(l.scope_text ?? ''),
          internal_estimate: scalar(l.internal_estimate ?? null),
          quote_amount: null,
          use_quote: false,
          job_item_id: itemId,
          markup_pct: l.markup_pct === undefined || l.markup_pct === null || l.markup_pct === '' ? null : (l.markup_pct as number | string),
          build_overrides: l.build_overrides && typeof l.build_overrides === 'object' && !Array.isArray(l.build_overrides) ? { ...(l.build_overrides as Record<string, SubLineBuildCell>) } : null,
        });
        applied.push(s.id);
        break;
      }
      case 'sub_line_edit': {
        // one thing on a sub line (2026-10-25); the words and the vendor move no price, the rest is laid over the line
        const line = subLines.find((l) => l.id === s.target_id);
        if (!line) {
          skipped.push({ id: s.id, why: 'its sub line is no longer on the job' });
          break;
        }
        const cells = { ...(line.build_overrides ?? {}) } as Record<string, SubLineBuildCell>;
        const put = (id: string, cell: SubLineBuildCell) => {
          if (Object.keys(cell).length === 0) delete cells[id];
          else cells[id] = cell;
        };
        if (s.target_key === 'scope_text') line.scope_text = String(s.new_value ?? line.scope_text);
        else if (s.target_key === 'markup_pct') line.markup_pct = s.new_value === null || s.new_value === undefined || s.new_value === '' ? null : (s.new_value as number | string);
        else if (s.target_key === 'job_item_id') line.job_item_id = typeof s.new_value === 'string' && s.new_value ? s.new_value : null;
        else if (s.target_key === 'builds') {
          const into = Array.isArray(s.new_value) ? (s.new_value as unknown[]).map(String) : [];
          const every = Array.isArray(s.payload.all_build_ids) ? (s.payload.all_build_ids as unknown[]).map(String) : into;
          for (const id of every) {
            const { left_out: _l, ...rest } = cells[id] ?? {};
            put(id, into.includes(id) ? rest : { ...rest, left_out: true });
          }
        } else if (s.target_key === 'build_amount') {
          const id = String(s.payload.build_id ?? '');
          const { amount: _a, ...rest } = cells[id] ?? {};
          const v = s.new_value === null || s.new_value === undefined ? '' : String(s.new_value).trim();
          put(id, v === '' ? rest : { ...rest, amount: v });
        }
        line.build_overrides = cells;
        applied.push(s.id);
        break;
      }
      case 'scope_reword':
      case 'scope_link':
      case 'scope_add':
      case 'scope_dismiss':
        // a scope row is words, not price (2026-10-18): nothing to lay over the job, and nothing left out of the preview
        applied.push(s.id);
        break;
      default:
        skipped.push({ id: s.id, why: 'a library suggestion changes the library, not this job' });
    }
  }
  const lists = {
    ...(jobIn.routeLines !== undefined || touched.routeLines ? { routeLines } : {}),
    ...(jobIn.bomLines !== undefined || touched.bomLines ? { bomLines } : {}),
    ...(jobIn.costLines !== undefined || touched.costLines ? { costLines } : {}),
  };
  return { jobIn: { ...jobIn, items, subLines, standards, ...lists }, applied, skipped, materials };
}

/** A job item as a suggestion describes it: a catalog item with inputs typed, nothing else. */
function bareItem(id: string, p: Record<string, unknown>, sort: number): JobItemIn {
  return {
    id,
    number: String(p.number ?? ''),
    name: String(p.name ?? ''),
    catalog_item_id: typeof p.catalog_item_id === 'string' ? p.catalog_item_id : null,
    recipe_id: null,
    parent_item_id: null,
    room_id: typeof p.room_id === 'string' ? p.room_id : null,
    child_index: null,
    inputs: p.inputs && typeof p.inputs === 'object' ? { ...(p.inputs as Record<string, Scalar>) } : {},
    overrides: {},
    is_custom: false,
    custom_route: [],
    custom_bom: [],
    note: null,
    sort,
  };
}

/** A custom line as the custom item keeps it, at the unit cost in the box (the same as ug.custom_line_of). */
export function customLine(raw: unknown, unitCost: unknown): CustomBomLine {
  const l = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const t = (v: unknown) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim());
  return {
    material_id: null,
    label: t(l.label),
    qty: (l.qty as Scalar) ?? '1',
    unit_cost: t(unitCost) ?? t(l.unit_cost),
    vendor: t(l.vendor),
    quote_date: t(l.quote_date),
    link: t(l.link),
    note: t(l.note),
  };
}

// ---------- a what-if: the job priced with a change it does not have, never stored (2026-10-17) ----------

export const PERCENTAGE_KEYS = ['labor_contingency_pct', 'materials_markup_pct', 'sub_markup_pct', 'gross_margin_pct', 'sales_tax_pct', 'estimate_range_down_pct', 'estimate_range_up_pct'] as const;

/** One change to price: an input typed on an item, a job standard, or one of the job's pricing percentages. */
export type WhatIfChange =
  | { kind: 'input'; item_id: string; number?: string; key: string; value: Scalar }
  | { kind: 'standard'; key: string; value: Scalar }
  | { kind: 'percentage'; key: (typeof PERCENTAGE_KEYS)[number]; value: Scalar };

export interface WhatIf {
  changes: WhatIfChange[];
  /** Lay every waiting suggestion over the job first. */
  include_waiting?: boolean;
  /** Price both ways under this Build (the CO-01 switch moves the price only under the Cabinotch build); the caller turns it into the build's overrides. */
  build_id?: string | null;
  /** And with these Add-ons on. */
  add_on_ids?: string[];
}

/** The job with the what-if laid over its rows (and the waiting suggestions, when asked): the input for the second price. */
export function applyWhatIf(jobIn: JobIn, whatIf: WhatIf, suggestions: SuggestionIn[] = []): Applied {
  const base: Applied = whatIf.include_waiting ? applySuggestions(jobIn, suggestions) : { jobIn, applied: [], skipped: [], materials: [] };
  const items = base.jobIn.items.map((i) => ({ ...i, inputs: { ...i.inputs } }));
  const standards = { ...base.jobIn.standards };
  const pricing: Record<string, Scalar> = { ...((base.jobIn.pricing ?? {}) as Record<string, Scalar>) };
  const applied = [...base.applied];
  const skipped = [...base.skipped];
  (whatIf.changes ?? []).forEach((c, k) => {
    const id = `change ${k + 1}`;
    if (c.kind === 'input') {
      const item = items.find((i) => i.id === c.item_id);
      if (!item) return void skipped.push({ id, why: `${c.number ?? 'the item'} is not on the job` });
      item.inputs[c.key] = c.value;
    } else if (c.kind === 'standard') {
      standards[c.key] = c.value;
    } else if (c.kind === 'percentage') {
      if (!(PERCENTAGE_KEYS as readonly string[]).includes(c.key)) return void skipped.push({ id, why: `${String(c.key)} is not one of the job's percentages` });
      pricing[c.key] = c.value;
    } else {
      return void skipped.push({ id, why: 'not a change the what-if knows' });
    }
    applied.push(id);
  });
  return { jobIn: { ...base.jobIn, items, standards, pricing: pricing as JobIn['pricing'] }, applied, skipped, materials: base.materials };
}
